import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TypeaheadSelect } from '@/components/ui/TypeaheadSelect';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Clock, 
  Users, 
  Calendar,
  AlertTriangle,
  FileText,
  Filter,
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { fetchFacilitatorHours } from '../../lib/airtable';

// Cache key for facilitator hours data
const FACILITATOR_HOURS_CACHE_KEY = 'facilitator_hours_data';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Helper function to calculate optimal text color for contrast
function getContrastTextColor(backgroundColor) {
  if (!backgroundColor || backgroundColor === 'transparent') return 'white';
  
  let r, g, b;
  
  // Handle hex colors
  if (backgroundColor.startsWith('#')) {
    const hex = backgroundColor.slice(1);
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);  
      b = parseInt(hex[2] + hex[2], 16);
    } else {
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    }
  }
  // Handle rgb() format
  else if (backgroundColor.startsWith('rgb')) {
    const matches = backgroundColor.match(/\d+/g);
    if (matches && matches.length >= 3) {
      [r, g, b] = matches.slice(0, 3).map(Number);
    } else {
      return 'white';
    }
  }
  // Handle named colors
  else {
    const colorMap = {
      'red': [255, 0, 0],
      'blue': [0, 0, 255],
      'green': [0, 128, 0],
      'yellow': [255, 255, 0],
      'orange': [255, 165, 0],
      'purple': [128, 0, 128],
      'pink': [255, 192, 203],
      'brown': [165, 42, 42],
      'gray': [128, 128, 128],
      'grey': [128, 128, 128],
      'black': [0, 0, 0],
      'white': [255, 255, 255],
      'lightpink': [255, 182, 193],
      'lightyellow': [255, 255, 224],
      'lightblue': [173, 216, 230],
      'lightgreen': [144, 238, 144],
      'darkred': [139, 0, 0],
      'darkblue': [0, 0, 139],
      'darkgreen': [0, 100, 0]
    };
    
    const color = colorMap[backgroundColor.toLowerCase()];
    if (color) {
      [r, g, b] = color;
    } else {
      return 'white'; // Default for unknown colors
    }
  }
  
  // Calculate relative luminance using WCAG formula
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  
  // Use WCAG contrast threshold (0.5 is a good balance)
  return luminance > 0.5 ? 'black' : 'white';
}

const FacilitatorHoursView = () => {
  const [facilitatorHoursData, setFacilitatorHoursData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    program: '',
    facilitator: '',
    client: '',
    soNumber: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination state
  const [displayLimit, setDisplayLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [allData, setAllData] = useState([]);
  
  // Sorting state - default to most recent first
  const [sortConfig, setSortConfig] = useState({
    key: 'deliveryStart',
    direction: 'desc'
  });

  useEffect(() => {
    loadFacilitatorHours();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  async function loadFacilitatorHours() {
    // Check for cached data first
    try {
      const cached = sessionStorage.getItem(FACILITATOR_HOURS_CACHE_KEY);
      if (cached) {
        const { timestamp, data } = JSON.parse(cached);
        
        // Validate cached data structure
        if (Array.isArray(data) && data.length > 0) {
          const sampleRecord = data[0];
          const hasRequiredFields = sampleRecord.soNumber && 
                                   sampleRecord.clientName && 
                                   Array.isArray(sampleRecord.facilitators);
                                   
          if (hasRequiredFields && Date.now() - timestamp < CACHE_TTL) {
            process.env.NODE_ENV === 'development' && console.log('📋 Loading facilitator hours from cache');
            setAllData(data);
            setFacilitatorHoursData(data);
            setLoading(false);
            return;
          } else if (!hasRequiredFields) {
            console.warn('⚠️ Cached data structure invalid, refetching...');
            sessionStorage.removeItem(FACILITATOR_HOURS_CACHE_KEY);
          }
        } else if (Date.now() - timestamp >= CACHE_TTL) {
          sessionStorage.removeItem(FACILITATOR_HOURS_CACHE_KEY);
        }
      }
    } catch (error) {
      console.warn('⚠️ Cache validation failed:', error);
      // Clear corrupted cache
      try {
        sessionStorage.removeItem(FACILITATOR_HOURS_CACHE_KEY);
      } catch (clearError) {
        console.warn('Failed to clear corrupted cache:', clearError);
      }
    }

    // Performance monitoring
    const startTime = performance.now();
    
    try {
      process.env.NODE_ENV === 'development' && console.log('🔄 Fetching facilitator hours data...');
      setLoading(true);
      
      // Fetch all facilitator hours (no API filters for now, we'll filter client-side)
      const data = await fetchFacilitatorHours({});
      
      // Validate API response structure
      if (!Array.isArray(data)) {
        throw new Error('Invalid API response: Expected array of facilitator hours data');
      }
      
      // Validate data structure for first few records
      if (data.length > 0) {
        const sampleSize = Math.min(3, data.length);
        for (let i = 0; i < sampleSize; i++) {
          const record = data[i];
          if (!record.soNumber || !record.clientName || !Array.isArray(record.facilitators)) {
            console.warn(`⚠️ Record ${i} missing required fields:`, {
              hasSoNumber: !!record.soNumber,
              hasClientName: !!record.clientName,
              hasFacilitators: Array.isArray(record.facilitators)
            });
          }
        }
      }
      
      const loadTime = performance.now() - startTime;
      process.env.NODE_ENV === 'development' && console.log(`✅ Loaded ${data.length} SOs with facilitator hours in ${loadTime.toFixed(2)}ms`);
      
      // Send performance metrics to monitoring (GA4/Sentry) if available
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'facilitator_hours_load', {
          event_category: 'performance',
          value: Math.round(loadTime),
          so_count: data.length,
          custom_parameter_1: loadTime < 3000 ? 'fast' : 'slow'
        });
      }
      
      setAllData(data);
      setFacilitatorHoursData(data);

      // Cache the data for future visits
      try {
        const cacheData = {
          timestamp: Date.now(),
          data: data
        };
        sessionStorage.setItem(FACILITATOR_HOURS_CACHE_KEY, JSON.stringify(cacheData));
      } catch (cacheError) {
        // Failed to cache data - not critical, continue
        console.warn('Failed to cache facilitator hours data:', cacheError);
      }
    } catch (err) {
      const loadTime = performance.now() - startTime;
      console.error(`❌ FacilitatorHoursView: Load failed after ${loadTime.toFixed(2)}ms`, err);
      
      // Track errors in monitoring
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'facilitator_hours_error', {
          event_category: 'error',
          error_message: err.message || 'Unknown error'
        });
      }
      
      setError(err.message || 'Failed to load facilitator hours data');
    } finally {
      setLoading(false);
    }
  }

  // Apply client-side filters when they change - optimized single pass
  useEffect(() => {
    if (allData.length === 0) return;
    
    const startTime = performance.now();
    
    // Parse filter values once
    const startDate = filters.startDate ? new Date(filters.startDate) : null;
    const endDate = filters.endDate ? new Date(filters.endDate) : null;
    const programFilter = filters.program?.toLowerCase() || null;
    const clientFilter = filters.client?.toLowerCase() || null;
    const facilitatorFilter = filters.facilitator?.toLowerCase() || null;
    const soNumberFilter = filters.soNumber?.toLowerCase() || null;
    
    // Single-pass filtering for better performance
    const filtered = allData.filter(item => {
      // Date filters
      if (startDate && item.deliveryStart) {
        if (new Date(item.deliveryStart) < startDate) return false;
      }
      
      if (endDate && item.deliveryStart) {
        if (new Date(item.deliveryStart) > endDate) return false;
      }
      
      // Program filter
      if (programFilter) {
        if (!item.programName?.toLowerCase().includes(programFilter)) return false;
      }
      
      // Client filter
      if (clientFilter) {
        if (!item.clientName?.toLowerCase().includes(clientFilter)) return false;
      }
      
      // Facilitator filter
      if (facilitatorFilter) {
        const hasFacilitator = item.facilitators.some(f => 
          f.name?.toLowerCase().includes(facilitatorFilter)
        );
        if (!hasFacilitator) return false;
      }
      
      // SO Number filter
      if (soNumberFilter) {
        if (!item.soNumber?.toLowerCase().includes(soNumberFilter)) return false;
      }
      
      return true;
    });
    
    const filterTime = performance.now() - startTime;
    if (process.env.NODE_ENV === 'development' && filterTime > 50) {
      console.log(`⚡ Filter processing took ${filterTime.toFixed(2)}ms for ${allData.length} records -> ${filtered.length} results`);
    }
    
    setFacilitatorHoursData(filtered);
  }, [allData, filters]);

  // Get unique values for filter dropdowns from all data - optimized single pass
  const filterOptions = useMemo(() => {
    if (allData.length === 0) return {
      programs: [],
      clients: [],
      facilitators: [],
      soNumbers: []
    };
    
    const startTime = performance.now();
    
    // Single pass to collect all unique values
    const programSet = new Set();
    const clientSet = new Set();
    const facilitatorSet = new Set();
    const soNumberSet = new Set();
    
    allData.forEach(item => {
      if (item.programName) programSet.add(item.programName);
      if (item.clientName) clientSet.add(item.clientName);
      if (item.soNumber) soNumberSet.add(item.soNumber);
      
      // Collect facilitator names
      item.facilitators.forEach(f => {
        if (f.name) facilitatorSet.add(f.name);
      });
    });
    
    // Convert to sorted arrays
    const programs = Array.from(programSet)
      .sort()
      .map((name, index) => ({ id: index, name }));
      
    const clients = Array.from(clientSet)
      .sort()
      .map((name, index) => ({ id: index, name }));
      
    const facilitators = Array.from(facilitatorSet)
      .sort()
      .map((name, index) => ({ id: index, name }));
      
    const soNumbers = Array.from(soNumberSet)
      .sort((a, b) => b.localeCompare(a)) // Most recent first
      .map((name, index) => ({ id: index, name }));
    
    const optionsTime = performance.now() - startTime;
    if (process.env.NODE_ENV === 'development' && optionsTime > 50) {
      console.log(`⚡ Filter options processing took ${optionsTime.toFixed(2)}ms`);
    }
    
    return {
      programs,
      clients,
      facilitators,
      soNumbers
    };
  }, [allData]);

  // Sort and paginate data
  const sortedAndPaginatedData = useMemo(() => {
    const sorted = [...facilitatorHoursData];
    
    sorted.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle special sorting cases
      if (sortConfig.key === 'deliveryStart' || sortConfig.key === 'deliveryEnd') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else if (sortConfig.key === 'totalHours' || sortConfig.key === 'totalSessions') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else if (sortConfig.key === 'primaryFacilitator') {
        aVal = a.primaryFacilitator?.name || 'Unassigned';
        bVal = b.primaryFacilitator?.name || 'Unassigned';
      } else {
        aVal = String(aVal || '');
        bVal = String(bVal || '');
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    // Apply pagination
    const startIndex = (currentPage - 1) * displayLimit;
    const endIndex = startIndex + displayLimit;
    return sorted.slice(startIndex, endIndex);
  }, [facilitatorHoursData, sortConfig, displayLimit, currentPage]);

  // Calculate summary statistics from filtered data - optimized single pass
  const summaryStats = useMemo(() => {
    const isFiltered = Object.values(filters).some(value => value !== '');
    
    if (facilitatorHoursData.length === 0) {
      return {
        totalSOs: 0,
        totalHours: 0,
        totalSessions: 0,
        uniqueFacilitators: 0,
        isFiltered
      };
    }
    
    const startTime = performance.now();
    
    // Single pass calculation
    let totalHours = 0;
    let totalSessions = 0;
    const facilitatorSet = new Set();
    
    facilitatorHoursData.forEach(item => {
      totalHours += item.totalHours || 0;
      totalSessions += item.totalSessions || 0;
      
      // Add facilitator IDs or names to set for unique count
      item.facilitators.forEach(f => {
        if (f.id) {
          facilitatorSet.add(f.id);
        } else if (f.name) {
          facilitatorSet.add(f.name);
        }
      });
    });
    
    const statsTime = performance.now() - startTime;
    if (process.env.NODE_ENV === 'development' && statsTime > 10) {
      console.log(`⚡ Summary stats calculation took ${statsTime.toFixed(2)}ms`);
    }
    
    return {
      totalSOs: facilitatorHoursData.length,
      totalHours,
      totalSessions,
      uniqueFacilitators: facilitatorSet.size,
      isFiltered
    };
  }, [facilitatorHoursData, filters]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-3 w-3 opacity-30" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-3 w-3" />
      : <ArrowDown className="h-3 w-3" />;
  };

  // Export functionality - only export filtered data
  const exportToCSV = () => {
    const headers = [
      'SO Number', 'Client', 'Program', 'Status',
      'Delivery Start', 'Delivery End', 'Total Hours',
      'Primary Facilitator', 'All Facilitators'
    ];
    
    const rows = facilitatorHoursData.map(item => [
      item.soNumber,
      item.clientName,
      item.programName,
      item.soStatus,
      item.deliveryStart ? new Date(item.deliveryStart).toLocaleDateString() : '',
      item.deliveryEnd ? new Date(item.deliveryEnd).toLocaleDateString() : '',
      item.totalHours,
      item.primaryFacilitator?.name || 'Unassigned',
      item.facilitators.map(f => f.name).join('; ')
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `facilitator-hours-filtered-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Handle facilitator click to filter
  const handleFacilitatorClick = (facilitatorName) => {
    setFilters(prev => ({
      ...prev,
      facilitator: facilitatorName
    }));
  };
  
  // Handle client click to filter
  const handleClientClick = (clientName) => {
    setFilters(prev => ({
      ...prev,
      client: clientName
    }));
  };

  // Handle program click to filter
  const handleProgramClick = (programName) => {
    setFilters(prev => ({
      ...prev,
      program: programName
    }));
  };
  
  // Clear a specific filter
  const clearFilter = (filterKey) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: ''
    }));
  };
  
  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      program: '',
      facilitator: '',
      client: '',
      soNumber: ''
    });
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  // Pagination navigation functions
  const totalPages = Math.ceil(facilitatorHoursData.length / displayLimit);
  
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };
  
  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  // Reset to page 1 when displayLimit changes
  const handleDisplayLimitChange = (newLimit) => {
    setDisplayLimit(parseInt(newLimit));
    setCurrentPage(1);
  };

  // Calculate total hours for filtered facilitator
  const facilitatorTotalHours = useMemo(() => {
    if (!filters.facilitator) return null;
    
    let totalHours = 0;
    facilitatorHoursData.forEach(item => {
      const facilitator = item.facilitators.find(f => f.name === filters.facilitator);
      if (facilitator) {
        // Try to find specific hours for this facilitator
        const facilitatorHours = item.facilitatorHours.find(fh => fh.facilitatorId === facilitator.id);
        if (facilitatorHours && facilitatorHours.hours) {
          totalHours += facilitatorHours.hours;
        } else if (item.totalHours && item.facilitators.length > 0) {
          // Fallback to equal distribution
          totalHours += item.totalHours / item.facilitators.length;
        }
      }
    });
    
    return totalHours;
  }, [facilitatorHoursData, filters.facilitator]);

  if (loading) {
    return (
      <div className="space-y-6 p-4">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            <div>
              <p className="font-medium">Failed to load facilitator hours data</p>
              <p className="text-sm text-gray-600">{error}</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2 min-h-11"
                onClick={loadFacilitatorHours}
              >
                Try Again
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (facilitatorHoursData.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="flex flex-col items-center gap-4">
            <Clock className="h-12 w-12 text-gray-400" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">No Facilitator Hours Data</h3>
              <p className="text-gray-500 mt-1">
                There are currently no scheduled offerings with facilitator hour data to display.
              </p>
              <p className="text-gray-400 text-xs">
                Try adjusting your filters or check back later for new data.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Facilitator Hours</h1>
          <p className="text-gray-500 mt-1">
            Analyze facilitator workload and hours across scheduled offerings
            {summaryStats.isFiltered && <span className="text-blue-600 font-medium"> (filtered)</span>}
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 min-h-11"
          >
            <Filter className="h-4 w-4" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={exportToCSV}
            className="flex items-center gap-2 min-h-11"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{summaryStats.totalSOs}</p>
                <p className="text-xs text-gray-500">{summaryStats.isFiltered ? 'Filtered SOs' : 'Total SOs'}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-green-600">{summaryStats.totalHours.toFixed(1)}</p>
                <p className="text-xs text-gray-500">{summaryStats.isFiltered ? 'Filtered Hours' : 'Total Hours'}</p>
              </div>
              <Clock className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{summaryStats.totalSessions}</p>
                <p className="text-xs text-gray-500">{summaryStats.isFiltered ? 'Filtered Sessions' : 'Total Sessions'}</p>
              </div>
              <Calendar className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{summaryStats.uniqueFacilitators}</p>
                <p className="text-xs text-gray-500">{summaryStats.isFiltered ? 'Active Facilitators' : 'Total Facilitators'}</p>
              </div>
              <Users className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Filters Display */}
      {summaryStats.isFiltered && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-blue-800">Active Filters:</span>
              {filters.startDate && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  Start: {filters.startDate}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter('startDate')} />
                </Badge>
              )}
              {filters.endDate && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  End: {filters.endDate}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter('endDate')} />
                </Badge>
              )}
              {filters.client && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  Client: {filters.client}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter('client')} />
                </Badge>
              )}
              {filters.program && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  Program: {filters.program}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter('program')} />
                </Badge>
              )}
              {filters.facilitator && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  Facilitator: {filters.facilitator}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter('facilitator')} />
                </Badge>
              )}
              {filters.soNumber && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  SO: {filters.soNumber}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter('soNumber')} />
                </Badge>
              )}
              <Button variant="outline" size="sm" className="min-h-11" onClick={clearAllFilters}>
                Clear All
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters Section */}
      {showFilters && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Filter className="h-5 w-5" />
                Filters
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(false)}
                className="h-8 w-8 p-0 hover:bg-gray-100"
                title="Hide filters"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Date Range */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
            </div>
            
            {/* Type-ahead Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <TypeaheadSelect
                label="Client"
                options={filterOptions.clients}
                value={filters.client}
                onChange={(value) => setFilters(prev => ({ ...prev, client: value }))}
                placeholder="Search clients..."
              />
              
              <TypeaheadSelect
                label="Program"
                options={filterOptions.programs}
                value={filters.program}
                onChange={(value) => setFilters(prev => ({ ...prev, program: value }))}
                placeholder="Search programs..."
              />
              
              <TypeaheadSelect
                label="Facilitator"
                options={filterOptions.facilitators}
                value={filters.facilitator}
                onChange={(value) => setFilters(prev => ({ ...prev, facilitator: value }))}
                placeholder="Search facilitators..."
              />
              
              <TypeaheadSelect
                label="SO Number"
                options={filterOptions.soNumbers}
                value={filters.soNumber}
                onChange={(value) => setFilters(prev => ({ ...prev, soNumber: value }))}
                placeholder="Search SO numbers..."
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle>Facilitator Hours</CardTitle>
          {/* Facilitator Total Hours Summary */}
          {filters.facilitator && facilitatorTotalHours !== null && (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-900">
                  Total Hours for {filters.facilitator}:
                </span>
                <span className="text-lg font-bold text-blue-600">
                  {facilitatorTotalHours.toFixed(1)}h
                </span>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <button 
                      className="flex items-center gap-1 w-full text-left hover:bg-gray-50 focus:bg-gray-50 rounded p-1"
                      onClick={() => handleSort('soNumber')}
                      onKeyDown={(e) => e.key === 'Enter' && handleSort('soNumber')}
                      aria-label="Sort by SO Number"
                    >
                      SO {getSortIcon('soNumber')}
                    </button>
                  </TableHead>
                  <TableHead>
                    <button 
                      className="flex items-center gap-1 w-full text-left hover:bg-gray-50 focus:bg-gray-50 rounded p-1"
                      onClick={() => handleSort('clientName')}
                      onKeyDown={(e) => e.key === 'Enter' && handleSort('clientName')}
                      aria-label="Sort by Client Name"
                    >
                      Client {getSortIcon('clientName')}
                    </button>
                  </TableHead>
                  <TableHead>
                    <button 
                      className="flex items-center gap-1 w-full text-left hover:bg-gray-50 focus:bg-gray-50 rounded p-1"
                      onClick={() => handleSort('programName')}
                      onKeyDown={(e) => e.key === 'Enter' && handleSort('programName')}
                      aria-label="Sort by Program Name"
                    >
                      Program {getSortIcon('programName')}
                    </button>
                  </TableHead>
                  <TableHead>
                    <button 
                      className="flex items-center gap-1 w-full text-left hover:bg-gray-50 focus:bg-gray-50 rounded p-1"
                      onClick={() => handleSort('deliveryStart')}
                      onKeyDown={(e) => e.key === 'Enter' && handleSort('deliveryStart')}
                      aria-label="Sort by Dates"
                    >
                      Dates {getSortIcon('deliveryStart')}
                    </button>
                  </TableHead>
                  <TableHead>
                    <button 
                      className="flex items-center gap-1 w-full text-left hover:bg-gray-50 focus:bg-gray-50 rounded p-1"
                      onClick={() => handleSort('primaryFacilitator')}
                      onKeyDown={(e) => e.key === 'Enter' && handleSort('primaryFacilitator')}
                      aria-label="Sort by Primary Facilitator"
                    >
                      Facilitators {getSortIcon('primaryFacilitator')}
                    </button>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedAndPaginatedData.map((item, index) => (
                  <TableRow key={item.soNumber || index}>
                    <TableCell>
                      <div className="font-medium">{item.soNumber}</div>
                      <div className="text-xs text-gray-500">{item.soStatus}</div>
                    </TableCell>
                    <TableCell>
                      <div 
                        className="font-medium cursor-pointer hover:text-blue-600 hover:underline"
                        onClick={() => handleClientClick(item.clientName)}
                        title={`Filter by client: ${item.clientName}`}
                      >
                        {item.clientName}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div 
                        className="max-w-40 truncate cursor-pointer hover:text-blue-600 hover:underline" 
                        title={`Filter by program: ${item.programName}`}
                        onClick={() => handleProgramClick(item.programName)}
                      >
                        {item.programName}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {item.deliveryStart ? new Date(item.deliveryStart).toLocaleDateString() : 'TBA'}
                      </div>
                      {item.deliveryEnd && (
                        <div className="text-xs text-gray-500">
                          to {new Date(item.deliveryEnd).toLocaleDateString()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.facilitators.length > 0 ? (
                        <div className="space-y-1">
                          {(() => {
                            // Build facilitator hours map once per row for O(1) lookups
                            const facilitatorHoursMap = new Map(
                              item.facilitatorHours.map(fh => [fh.facilitatorId, fh])
                            );
                            
                            // Filter facilitators based on current filter - show only filtered facilitator if filter is active
                            const facilitatorsToShow = filters.facilitator 
                              ? item.facilitators.filter(f => f.name === filters.facilitator)
                              : item.facilitators;
                            
                            return facilitatorsToShow.map((facilitator) => {
                              const facilitatorHours = facilitatorHoursMap.get(facilitator.id);
                              const bgColor = facilitator.color || '#6B7280';
                              const textColor = getContrastTextColor(bgColor);
                              
                              // Calculate individual hours - use actual data if available, otherwise assume equal distribution
                              let individualHours = 0;
                              if (facilitatorHours && facilitatorHours.hours) {
                                individualHours = facilitatorHours.hours;
                              } else if (item.totalHours && item.facilitators.length > 0) {
                                // Assume equal distribution until session-level data is available
                                individualHours = item.totalHours / item.facilitators.length;
                              }
                              
                              return (
                                <div key={facilitator.id} className="flex items-center justify-between">
                                  <Badge 
                                    variant="outline"
                                    className="text-xs cursor-pointer hover:opacity-80 border-0 font-medium"
                                    style={{ 
                                      backgroundColor: bgColor,
                                      color: textColor
                                    }}
                                    onClick={() => handleFacilitatorClick(facilitator.name)}
                                  >
                                    {facilitator.name}
                                  </Badge>
                                  <span className="text-xs font-semibold text-gray-700 ml-2">
                                    {individualHours.toFixed(1)}h
                                  </span>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      ) : (
                        <Badge variant="outline" className="text-xs text-gray-500">
                          Unassigned
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {/* Pagination */}
          {facilitatorHoursData.length > 10 && (
            <div className="mt-4 space-y-3">
              {/* Pagination Controls */}
              <div className="flex items-center justify-center gap-4">
                <div className="flex items-center gap-2">
                  {/* Previous Page Button */}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={goToPrevPage}
                    disabled={currentPage === 1}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  {/* Page Info */}
                  <span className="text-sm text-gray-600 min-w-fit">
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  {/* Next Page Button */}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Items Per Page Selector */}
                <div className="flex items-center gap-2">
                  <Label htmlFor="pagination-select" className="text-sm">Show:</Label>
                  <select
                    id="pagination-select"
                    className="px-2 py-1 border rounded text-sm"
                    value={displayLimit}
                    onChange={(e) => handleDisplayLimitChange(e.target.value)}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={facilitatorHoursData.length}>All ({facilitatorHoursData.length})</option>
                  </select>
                </div>
              </div>
              
              {/* Records Info */}
              <div className="text-center">
                <span className="text-xs text-gray-500">
                  Showing {((currentPage - 1) * displayLimit) + 1} to {Math.min(currentPage * displayLimit, facilitatorHoursData.length)} of {facilitatorHoursData.length} records
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FacilitatorHoursView;